import React, {useEffect, useState, lazy, Suspense} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useSession } from '../../Components/Session';
import { Navigate } from "react-router-dom";
import { Roles } from '../../Data/Users';
import Section, { Sections } from '../../Components/Containers/Section';
import { PageLoading } from '../../Components/Loading';
import Badge from '../../Components/Badge/Badge';
import Address from '../../Components/Formatting/Address';
import { useVerification } from '../../Data/Verification';
import Modal from '../../Components/Modal';
import Button from '../../Components/Button/Button';
import Form, { Actions, Conditional, TextField } from '../../Components/Form';
import ButtonRow from '../../Components/Button/ButtonRow';
import Alert from '../../Components/Alert';
import { useVisibilityChange } from '../../Services/helpers';
import PhoneNumber from '../../Components/Formatting/PhoneNumber';

const StripeIdentityVerification = lazy(
    () => import(/* webpackChunkName: "stripe" */'./StripeIdentityVerification')
);

const Verification = () => {
    const {isRoleSufficient} = useSession();
    const {verification, refresh, verifyPhoneNumber, verifyEmail, verifyIdentity, verifyAddress} = useVerification();
    const [requesting, setRequesting] = useState(false);

    useVisibilityChange(refresh, true);

    if (!isRoleSufficient(Roles.Owner)) {
        return <Navigate to="/more" />
    }

    return <>
        <TopBar left="BackBtn" title="Verification" />
        <Page>
            {verification ? <Inner {...{verification, setRequesting}} /> : <PageLoading />}
        </Page>

        <Requesting {...{requesting, refresh, setRequesting, verification, verifyPhoneNumber, verifyEmail, verifyIdentity, verifyAddress}} />
    </>
}

const Inner = ({verification, setRequesting}) => {
    return <Sections>
        <Section title="Email address" button={<VerificationBadge status={verification.emailStatus} />}>
            <p>{verification.email}</p>
            <RequestButton status={verification.emailStatus} setRequesting={() => setRequesting('email')} />
        </Section>

        <Section title="Phone number" button={<VerificationBadge status={verification.phoneNumberStatus} />}>
            <p><PhoneNumber phoneNumber={verification.phoneNumber} link={false} /></p>
            <RequestButton status={verification.phoneNumberStatus} setRequesting={() => setRequesting('phoneNumber')} />
        </Section>

        <Section title="Identity" button={<VerificationBadge status={verification.identityStatus} />}>
            <p>{verification.identity}</p>
            <RequestButton status={verification.identityStatus} setRequesting={() => setRequesting('identity')} />
            <IdentityFeedback status={verification.identityStatus} />
        </Section>

        <Section title="Address" button={<VerificationBadge status={verification.addressStatus} />}>
            <p><Address address={verification.address} /></p>
            <RequestButton status={verification.addressStatus} setRequesting={() => setRequesting('address')} />
        </Section>
    </Sections>
}

const IdentityFeedback = ({status}) => {
    if (status === 'Failed') {
        return <Alert theme="warning"
            title="Verification failed"
            message={<>
                Don't worry, this happens sometimes.<br />
                Please complete all other verifications then contact us to resolve this issue.
            </>}
        />
    }

    if (status === 'Processing') {
        return <Alert theme="info"
            title="Verification in progress"
            message="We are currently verifying your identity. We may need to contact you for more information."
        />
    }

    return null;
}

const Requesting = ({requesting, refresh, setRequesting, verification, verifyPhoneNumber, verifyEmail, verifyIdentity, verifyAddress}) => {
    let inner = <p>Unsupported verification request</p>

    switch (requesting) {
        case 'email':
            inner = <EmailVerification close={() => setRequesting(null)}
                    verification={verification}
                    refresh={refresh}
                    verifyEmail={verifyEmail}
                />
            break;
        case 'phoneNumber':
            inner = <PhoneNumberVerification close={() => setRequesting(null)}
                    verification={verification}
                    refresh={refresh}
                    verifyPhoneNumber={verifyPhoneNumber}
                />;
            break;
        case 'identity':
            inner = <IdentityVerification close={() => setRequesting(null)}
                    verification={verification}
                    refresh={refresh}
                    verifyIdentity={verifyIdentity}
                />;
            break;
        case 'address':
            inner = <AddressVerification close={() => setRequesting(null)}
                    verification={verification}
                    refresh={refresh}
                    verifyAddress={verifyAddress}
                />;
            break;
        default:
            inner = <p>Unsupported verification request</p>
            break
    }

    const titles = {
        email: 'Email verification',
        phoneNumber: 'Phone number verification',
        identity: 'Identity verification',
        address: 'Address verification',
    };

    return <Modal isOpen={!!requesting} setIsOpen={setRequesting} title={titles[requesting]} dismissible>
        {inner}
    </Modal>
}

const EmailVerification = ({close, verification, refresh, verifyEmail}) => {

    return <>
        <div className="prose-sm text-muted">
            <p>We emailed you a link when you opened you account, please click this link to verify your email address.</p>
            <p>If you need to change your email address you can do this by editing your user profile.</p>
        </div>

        <ButtonRow arrange="right">
            <Button theme="light"
                label="Resend link"
                onClick={async () => {
                    await verifyEmail();
                    close();
                }}
                onClickLoading
            />
            <Button theme="light" label="Close" onClick={() => close()} />
        </ButtonRow>
    </>
}

const IdentityVerification = ({close, verification, refresh, verifyIdentity}) => {
    const [verifying, setVerifying] = useState(false);

    const doVerification = async () => {
        setVerifying(true);
        await verifyIdentity();
    }

    const handleVerification = async () => {
        await verifyIdentity();
        close();
    }

    return <>
        <div className="prose-sm text-muted">
            <p>We use an independent service (Stripe) to help verify your identity.</p>

            <p>You will need to take a picture of an official document with your photo (e.g. a driving licence or passport), and take a live selfie.</p>

            <p>It's best to complete this using a mobile phone rather than a computer.</p>
        </div>

        {(verifying && verification && verification?.identitySession) && <Suspense fallback={null}>
            <StripeIdentityVerification session={verification?.identitySession ?? null}
                onSuccess={handleVerification}
                onError={handleVerification}
            />
        </Suspense>}

        <ButtonRow arrange="right">
            <Button theme="light"
                label="Close"
                onClick={() => close()}
                disabled={verifying}
            />
            <Button theme="primary"
                label="Verify identity"
                onClick={doVerification}
                onClickLoading
                loading={verifying}
                disabled={verifying}
            />
        </ButtonRow>
    </>
}

let smsResendTimeout;
const PhoneNumberVerification = ({close, verification, refresh, verifyPhoneNumber}) => {
    const [resendCodeIn, setResendCodeIn] = useState(null);

    useEffect(() => {
        if (verification.phoneNumberStatus === 'Verified') {
            close();
            return;
        }
        if (verification.phoneNumberStatus !== 'Pending') {
            return;
        }

        sendVerificationCode();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verification.phoneNumberStatus]);

    useEffect(() => {
        smsResendTimeout = setInterval(() => {
            setResendCodeIn(c => c ? c - 1 : null);
        }, 1000);

        return () => clearInterval(smsResendTimeout);
    }, []);

    const sendVerificationCode = async () => {
        await verifyPhoneNumber({request: true});
        setResendCodeIn(60);
    }

    return <Form onSubmit={verifyPhoneNumber}>
        <div className="prose-sm text-muted">
            <p>We have sent a verification code to your phone number. Please enter it below.</p>
            <p>If you need to change your phone number you can do this by editing your business details.</p>
        </div>

        <TextField name="code" label="Verification code" />
        <Actions submit={{label: 'Verify'}}
            cancel={{label: 'Close'}}
            extra={{
                className: 'btn-light',
                label: <>Resend code {resendCodeIn ? ` (${resendCodeIn})` : ''}</>,
                disabled: resendCodeIn !== null,
                onClick: () => sendVerificationCode(),
            }}
        />
    </Form>
}

const AddressVerification = ({close, verification, refresh, verifyAddress}) => {
    useEffect(() => {
        if (verification.addressStatus === 'Verified') {
            close();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verification.addressStatus]);

    return <Form onSubmit={verifyAddress} data={{status: verification.addressStatus}} key={verification.addressStatus}>
        <Conditional condition={(formData) => formData.status === 'Available'}>
            <div className="prose-sm text-muted">
                <p>
                    To verify your address we will post a verification code to you.
                    This will take a few days to arrive.
                </p>
                <p>
                    Please make sure your address is correct before continuing - if you change it later you'll need to re-verify.
                </p>
                <p>
                    In most cases this is not necessary, but we might ask you to verify your address if there is high-risk activity on your payments account.
                </p>
                <p className="text-md text-black">
                    <Address address={verification.address} />
                </p>
            </div>
        </Conditional>

        <Conditional condition={(formData) => formData.status === 'Requested'}>
            <div className="prose-sm text-muted">
                <p>
                    A letter with your verification code is on it's way to you. Once it arrives you can enter the code below.
                </p>
            </div>

            <TextField name="code" label="Verification code" />
        </Conditional>

        <Actions submit={{label: verification.addressStatus === 'Requested' ? 'Verify' : 'Request'}}
            cancel={{label: 'Close'}}
        />
    </Form>
}

const VerificationBadge = ({status}) => {
    switch(status) {
        case 'Available':
            return <Badge theme="secondary" title="Optional" />
        case 'Pending':
            return <Badge theme="warning" title="Required" />
        case 'Verified':
            return <Badge theme="success" title="Verified" />
        case 'Failed':
            return <Badge theme="danger" title="Failed" />
        case 'Requested':
            return <Badge theme="info" title="In progress" />
        case 'Processing':
            return <Badge theme="info" title="Processing" />
        default:
            return null;
    }
}

const RequestButton = ({status, setRequesting}) => {
    const hidden = ['Verified', 'Processing', 'Failed', 'Unavailable'];

    if (hidden.includes(status)) {
        return null;
    }

    return <Button theme="light"
        label="Verify"
        onClick={setRequesting}
    />
}

export default Verification;

export {
    VerificationBadge
}